import React, { useCallback, useEffect, useState } from "react";
import { formatErr } from "@/lib/api";
import { dapi as api } from "./deskApi";
import { toast } from "sonner";
import { RefreshCw, Trash2, CheckCircle2, Clock, Eye, EyeOff, Copy, Mail, BellRing, FileText, Pencil } from "lucide-react";
import SignatureWorkbench from "@/components/SignatureWorkbench";

const TABS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Sent to sign" },
  { id: "signed", label: "Signed documents" },
];

/** Signature requests dashboard: per-signer tracking — sent, opened (when,
 * how many times), what they must sign, and signed status. */
export default function SignDocsApp() {
  const [reqs, setReqs] = useState(null);
  const [tab, setTab] = useState("all");
  const [editReq, setEditReq] = useState(null); // {req, pages}
  const [editBusy, setEditBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/desktop/signatures").then((r) => setReqs(r.data)).catch((e) => {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (s) => {
    if (!window.confirm("Delete this signature request? The signing links will stop working.")) return;
    try { await api.delete(`/desktop/signatures/${s.id}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied."); }
    catch { window.prompt("Copy the signing link:", link); }
  };

  const remind = async (sid, token) => {
    try {
      await api.post(`/desktop/signatures/${sid}/remind/${token}`);
      toast.success("Reminder email sent.");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const saveRemindSettings = async (sid, auto_remind, remind_days) => {
    try {
      await api.patch(`/desktop/signatures/${sid}`, { auto_remind, remind_days });
      setReqs((prev) => prev.map((r) => (r.id === sid ? { ...r, auto_remind, remind_days } : r)));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const viewDoc = async (s) => {
    const fid = s.status === "signed" && s.signed_file_id ? s.signed_file_id : s.file_id;
    try {
      const r = await api.get(`/desktop/fs/${fid}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open the document."); }
  };

  const startEdit = async (s) => {
    try {
      const r = await api.get(`/desktop/fs/${s.file_id}/pages`);
      setEditReq({ req: s, pages: r.data.pages || [] });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not load the document for editing."); }
  };

  const saveEdit = async ({ signers, message, fields, signing_order, auto_remind, remind_days, edits }) => {
    setEditBusy(true);
    try {
      await api.put(`/desktop/signatures/${editReq.req.id}/edit`, {
        message,
        signing_order,
        auto_remind,
        remind_days,
        edits,
        signers: editReq.req.signers.map((sg, i) => ({
          fields: fields.filter((f) => (f.signer ?? 0) === i).map(({ signer, ...f }) => f),
        })),
      });
      toast.success("Request updated — signers see the new version instantly.");
      setEditReq(null);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setEditBusy(false); }
  };

  const shown = (reqs || []).filter((s) =>
    tab === "all" ? true : tab === "signed" ? s.status === "signed" : s.status !== "signed"
  );

  return (
    <div className="h-full flex flex-col text-slate-800" data-testid="sign-app">
      <div className="flex items-center gap-1 px-2 pt-2 border-b border-slate-200 bg-slate-50">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-t border border-b-0 ${tab === t.id ? "bg-white border-slate-200 text-slate-800" : "bg-transparent border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`sign-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
        <button type="button" className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded hover:bg-slate-200" onClick={load} data-testid="sign-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        {!reqs && <p className="text-sm text-slate-400 text-center mt-8">Loading…</p>}
        {reqs && shown.length === 0 && (
          <p className="text-sm text-slate-400 text-center mt-8">
            {tab === "signed" ? "No fully signed documents yet." : tab === "pending" ? "Nothing waiting for signatures." : "No signature requests yet."}
          </p>
        )}
        {reqs && shown.map((s) => (
          <div key={s.id} className="border-b border-slate-100 px-3 py-2.5" data-testid={`sign-req-${s.id}`}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <span className="text-sm font-semibold truncate">{s.file_name}</span>
                <span className="text-xs text-slate-400 ml-2">
                  sent {fmtWhen(s.created_at)}
                  {s.signing_order === "sequential" ? " · signs in order" : ""}
                </span>
              </div>
              <StatusChip status={s.status} />
              <button type="button" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" onClick={() => viewDoc(s)}
                title="Open the document (works even if the file was moved)" data-testid={`sign-view-${s.id}`}>
                <FileText className="w-4 h-4" />
              </button>
              {s.status !== "signed" && (
                <button type="button" className="p-1.5 rounded hover:bg-sky-50 text-sky-600" onClick={() => startEdit(s)}
                  title="Edit this request — move boxes, erase, draw (pending signers only)" data-testid={`sign-edit-${s.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              <button type="button" className="p-1.5 rounded hover:bg-red-50 text-red-500" onClick={() => remove(s)} title="Delete request" data-testid={`sign-del-${s.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {(s.signers || []).length > 0 ? (
              <div className="mt-1.5 space-y-1">
                {s.signers.map((sg, i) => <SignerRow key={sg.token} sg={sg} i={i} copyLink={copyLink} remind={() => remind(s.id, sg.token)} />)}
              </div>
            ) : (
              <p className="text-xs text-slate-500 mt-1">
                {s.recipient_name || s.recipient_email || "Signer"}
                {s.opened_at ? ` · opened ${fmtWhen(s.opened_at)}` : " · not opened yet"}
              </p>
            )}
            {s.status !== "signed" && (
              <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-600" data-testid={`sign-remind-settings-${s.id}`}>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!s.auto_remind}
                    onChange={(e) => saveRemindSettings(s.id, e.target.checked, s.remind_days || 3)}
                    data-testid={`sign-auto-remind-${s.id}`}
                  />
                  Auto-remind every
                </label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  className="w-12 border border-slate-200 rounded px-1 py-0.5 text-[11px]"
                  value={s.remind_days || 3}
                  onChange={(e) => saveRemindSettings(s.id, !!s.auto_remind, Math.max(1, Math.min(60, parseInt(e.target.value) || 1)))}
                  data-testid={`sign-remind-days-${s.id}`}
                />
                days
                <span className="text-slate-400">{s.auto_remind ? "· on" : "· off"}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">
        Working copies live in "Sent to sign" — finished documents land in "Signed documents".
      </div>
      {editReq && (
        <SignatureWorkbench
          pages={editReq.pages}
          title={`Edit: ${editReq.req.file_name}`}
          allowSend
          desktopMode
          startPanel="send"
          lockSigners
          busy={editBusy}
          initialSigners={editReq.req.signers}
          initialMessage={editReq.req.message || ""}
          initialOrder={editReq.req.signing_order || "parallel"}
          initialAutoRemind={!!editReq.req.auto_remind}
          initialRemindDays={editReq.req.remind_days || 3}
          placeholders={editReq.req.signers.flatMap((sg, i) =>
            (sg.fields || []).map((f) => ({ ...f, signer: i }))
          )}
          onClose={() => setEditReq(null)}
          onSend={saveEdit}
          onSubmit={() => {}}
        />
      )}
    </div>
  );
}

function SignerRow({ sg, i, copyLink, remind }) {
  return (
    <div className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5" data-testid={`signer-row-${sg.token}`}>
      <span className="w-4 h-4 rounded-full bg-slate-700 text-white text-[9px] font-black flex items-center justify-center shrink-0">{i + 1}</span>
      <div className="min-w-0 flex-1">
        <span className="font-semibold">{sg.name || `Signer ${i + 1}`}</span>
        {sg.email && <span className="text-slate-400"> · {sg.email}</span>}
        <div className="text-[11px] text-slate-500" data-testid={`signer-fields-${sg.token}`}>
          To sign: {fieldSummary(sg.fields)}
        </div>
      </div>
      <span
        className={`inline-flex items-center gap-1 font-semibold whitespace-nowrap ${sg.opened_at ? "text-sky-700" : "text-slate-400"}`}
        title={sg.opened_at ? `First opened ${fmtWhen(sg.opened_at)} · last ${fmtWhen(sg.last_opened_at)} · ${sg.open_count || 1}×` : "Hasn't opened the link yet"}
        data-testid={`signer-opened-${sg.token}`}
      >
        {sg.opened_at
          ? <><Eye className="w-3.5 h-3.5" /> Opened {fmtWhen(sg.opened_at)}{(sg.open_count || 0) > 1 ? ` (${sg.open_count}×)` : ""}</>
          : <><EyeOff className="w-3.5 h-3.5" /> Not opened</>}
      </span>
      {sg.status === "signed" ? (
        <span className="inline-flex items-center gap-1 font-bold text-emerald-700 whitespace-nowrap" data-testid={`signer-status-${sg.token}`}>
          <CheckCircle2 className="w-3.5 h-3.5" /> Signed {fmtWhen(sg.signed_at)}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 font-bold text-amber-600 whitespace-nowrap" data-testid={`signer-status-${sg.token}`}>
          <Clock className="w-3.5 h-3.5" /> Waiting
        </span>
      )}
      {sg.status !== "signed" && sg.email && (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-amber-100 text-amber-700 font-semibold whitespace-nowrap"
          title={sg.last_reminded_at ? `Last reminded ${fmtWhen(sg.last_reminded_at)} — send another reminder email` : "Send a reminder email now"}
          onClick={remind}
          data-testid={`signer-remind-${sg.token}`}
        >
          <BellRing className="w-3.5 h-3.5" />
          {sg.last_reminded_at ? `Reminded ${fmtWhen(sg.last_reminded_at)}` : "Remind"}
        </button>
      )}
      {sg.link && (
        <button type="button" className="p-1 rounded hover:bg-slate-200" title="Copy signing link" onClick={() => copyLink(sg.link)} data-testid={`signer-copy-${sg.token}`}>
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  if (status === "signed") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
        <CheckCircle2 className="w-3.5 h-3.5" /> Completed
      </span>
    );
  }
  if (status === "partially_signed") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1">
        <Mail className="w-3.5 h-3.5" /> In progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
      <Clock className="w-3.5 h-3.5" /> Waiting
    </span>
  );
}

const KIND_LABELS = { signature: "signature", initials: "initials", text: "text box", date: "date", check: "checkbox" };

function fieldSummary(fields) {
  if (!fields || !fields.length) return "anything they want (no boxes prepared)";
  const counts = {};
  let optional = 0;
  for (const f of fields) {
    counts[f.kind] = (counts[f.kind] || 0) + 1;
    if (f.required === false) optional += 1;
  }
  const parts = Object.entries(counts).map(([k, n]) => `${n} ${KIND_LABELS[k] || k}${n > 1 ? (k === "check" ? "es" : "s") : ""}`);
  return parts.join(" · ") + (optional ? ` (${optional} optional)` : "");
}

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso.slice(0, 16); }
}
